import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './Dashboard.css';
import DispatchedSummary from './DispatchedSummary';
import PickerDetails from './PickerDetails';
import WarehouseComparison from './WarehouseComparison';
import PerformanceBanner from './PerformanceBanner';
import { getAPIEndpoint } from '../utils/api';
import { logout, getUserInfo, getAuthToken } from '../utils/auth';

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = getUserInfo();
  const [view, setView] = useState('summary'); // 'summary', 'details', 'comparison'
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [performance, setPerformance] = useState(null);

  // Always use database endpoint
  const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/dashboard` : 'http://localhost:3001/api/dashboard';

  // Get auth headers
  const getAuthHeaders = () => {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Filter states
  const [countries, setCountries] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [country, setCountry] = useState('All');
  const [orderType, setOrderType] = useState('All');
  const [excludeHQ, setExcludeHQ] = useState(false);
  const [excludeCarriers, setExcludeCarriers] = useState(false);

  // Map order type codes to readable labels
  const orderTypeLabels = {
    'A': 'Assemblies',
    'C': 'Customer',
    'T': 'Transfers'
  };

  // Load initial filter options
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Load summary data when filters change (not dates - dates require Apply button)
  useEffect(() => {
    if (startDate && endDate) {
      fetchSummaryData();
    }
  }, [country, orderType, excludeCarriers]);

  const fetchFilterOptions = async () => {
    try {
      const headers = getAuthHeaders();
      const [countriesRes, orderTypesRes] = await Promise.all([
        axios.get(`${API_URL}/countries`, { headers }),
        axios.get(`${API_URL}/order-types`, { headers })
      ]);
      setCountries(countriesRes.data.countries || []);
      setOrderTypes(orderTypesRes.data.orderTypes || []);
      // Load all data on open — no date filter, empty inputs
      fetchSummaryDataWithParams('', '', 'All', 'All', false, false);
    } catch (err) {
      console.error('Error fetching filter options:', err);
      if (err.response?.status === 401) {
        logout();
        window.location.href = '/login';
      } else {
        setError('Failed to load filter options. Please refresh the page.');
      }
    }
  };

  const fetchSummaryDataWithParams = async (start, end, ctry, ordType, exclHQ, excludeCarr) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (start) params.append('startDate', start);
      if (end) params.append('endDate', end);
      if (ctry && ctry !== 'All') params.append('country', ctry);
      if (ordType && ordType !== 'All') params.append('orderType', ordType);
      if (exclHQ) params.append('excludeHQ', 'true');
      if (excludeCarr) params.append('excludeCarriers', 'true');

      const headers = getAuthHeaders();
      const response = await axios.get(`${API_URL}/dispatched-summary?${params}`, { headers });
      setSummaryData(response.data);

      // Extract performance metrics
      if (response.data._performance) {
        setPerformance(response.data._performance);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching summary:', err);
      if (err.response?.status === 401) {
        logout();
        window.location.href = '/login';
      } else {
        setError('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSummaryData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (country && country !== 'All') params.append('country', country);
      if (orderType && orderType !== 'All') params.append('orderType', orderType);
      if (excludeHQ) params.append('excludeHQ', 'true');
      if (excludeCarriers) params.append('excludeCarriers', 'true');

      const headers = getAuthHeaders();
      const response = await axios.get(`${API_URL}/dispatched-summary?${params}`, { headers });
      setSummaryData(response.data);

      // Extract performance metrics
      if (response.data._performance) {
        setPerformance(response.data._performance);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching summary:', err);
      if (err.response?.status === 401) {
        logout();
        window.location.href = '/login';
      } else {
        setError('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchSummaryData();
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setCountry('All');
    setOrderType('All');
    setExcludeHQ(false);
    setExcludeCarriers(false);
    fetchSummaryDataWithParams('', '', 'All', 'All', false, false);
  };

  const handleWarehouseClick = (warehouse, isCtrlClick) => {
    if (isCtrlClick) {
      // Multi-select mode
      setSelectedWarehouses(prev => {
        if (prev.includes(warehouse)) {
          return prev.filter(w => w !== warehouse);
        } else {
          return [...prev, warehouse];
        }
      });
    } else {
      // Single select - go to details view
      if (selectedWarehouses.length === 0) {
        setSelectedWarehouse(warehouse);
        setView('details');
      }
    }
  };

  const handleBackToSummary = () => {
    setView('summary');
    setSelectedWarehouse(null);
    setSelectedWarehouses([]);
  };

  const handleCompareSelected = () => {
    if (selectedWarehouses.length > 0) {
      setView('comparison');
    }
  };

  const handleLogout = () => {
    logout();
    // Force reload to clear all state and redirect to login
    window.location.href = '/login';
  };

  if (loading && !summaryData) {
    return <div className="dashboard-loading">Loading...</div>;
  }

  if (error) {
    return <div className="dashboard-error">{error}</div>;
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>ITR America BI Reporting</h1>
          <div className="header-buttons">
            {userInfo && <span className="user-welcome">Welcome, {userInfo.fullName || userInfo.username}</span>}
            <button className="logout-btn" onClick={handleLogout}>Logout</button>
            <button className="back-home-btn" onClick={() => navigate('/')}>
              ← Back to Home
            </button>
          </div>
        </div>
        <h2>Dispatched Orders - Warehouse Performance</h2>
      </header>

      <PerformanceBanner performance={performance} />

      {view === 'summary' && (
        <>
          <div className="filters-section">
            <div className="filter-group">
              <label>Start Date:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  const value = e.target.value;
                  setStartDate(value);
                  if (value === '') {
                    setEndDate(''); // Auto-set end date to All when start date is All
                  }
                }}
              />
            </div>

            <div className="filter-group">
              <label>End Date:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label>Country:</label>
              <select value={country} onChange={(e) => setCountry(e.target.value)}>
                <option value="All">All</option>
                {countries.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Order Type:</label>
              <select value={orderType} onChange={(e) => setOrderType(e.target.value)}>
                <option value="All">All</option>
                {orderTypes.map(type => (
                  <option key={type} value={type}>
                    {orderTypeLabels[type] || type}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={excludeHQ}
                  onChange={(e) => setExcludeHQ(e.target.checked)}
                />
                Exclude Customers: HQ
              </label>
            </div>

            <div className="filter-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={excludeCarriers}
                  onChange={(e) => setExcludeCarriers(e.target.checked)}
                />
                Exclude Carriers:{' '}
                <span className="carrier-tip" data-tip="Hold for Instructions">Z</span>
                {'/'}
                <span className="carrier-tip" data-tip="Will Call">WC</span>
                {'/'}
                <span className="carrier-tip" data-tip="Hold For Stock Truck">HFSK</span>
              </label>
            </div>

            <button className="apply-filter-btn" onClick={handleApplyFilters}>
              Apply Filters
            </button>
            <button className="clear-filter-btn" onClick={handleClearFilters}>
              Clear Filters
            </button>
          </div>

          {selectedWarehouses.length > 0 && (
            <div className="multi-select-actions">
              <span className="selected-count">
                {selectedWarehouses.length} warehouse{selectedWarehouses.length !== 1 ? 's' : ''} selected
              </span>
              <button className="compare-btn" onClick={handleCompareSelected}>
                Compare Selected
              </button>
              <button className="clear-selection-btn" onClick={() => setSelectedWarehouses([])}>
                Clear Selection
              </button>
            </div>
          )}

          <DispatchedSummary
            data={summaryData}
            onWarehouseClick={handleWarehouseClick}
            selectedWarehouses={selectedWarehouses}
          />
        </>
      )}

      {view === 'details' && selectedWarehouse && (
        <PickerDetails
          warehouse={selectedWarehouse}
          startDate={startDate}
          endDate={endDate}
          country={country}
          orderType={orderType}
          excludeHQ={excludeHQ}
          excludeCarriers={excludeCarriers}
          apiUrl={API_URL}
          onBack={handleBackToSummary}
          onFilterChange={(updates) => {
            if ('startDate' in updates) setStartDate(updates.startDate);
            if ('endDate' in updates) setEndDate(updates.endDate);
            if ('country' in updates) setCountry(updates.country);
            if ('orderType' in updates) setOrderType(updates.orderType);
            if ('excludeHQ' in updates) setExcludeHQ(updates.excludeHQ);
            if ('excludeCarriers' in updates) setExcludeCarriers(updates.excludeCarriers);
          }}
        />
      )}

      {view === 'comparison' && selectedWarehouses.length > 0 && (
        <WarehouseComparison
          warehouses={selectedWarehouses}
          startDate={startDate}
          endDate={endDate}
          country={country}
          orderType={orderType}
          excludeHQ={excludeHQ}
          excludeCarriers={excludeCarriers}
          apiUrl={API_URL}
          onBack={handleBackToSummary}
          onFilterChange={(updates) => {
            if ('startDate' in updates) setStartDate(updates.startDate);
            if ('endDate' in updates) setEndDate(updates.endDate);
            if ('country' in updates) setCountry(updates.country);
            if ('orderType' in updates) setOrderType(updates.orderType);
            if ('excludeHQ' in updates) setExcludeHQ(updates.excludeHQ);
            if ('excludeCarriers' in updates) setExcludeCarriers(updates.excludeCarriers);
          }}
        />
      )}
    </div>
  );
}

export default Dashboard;
